import {
	autoUpdate,
	flip,
	offset,
	type Placement,
	safePolygon,
	shift,
	useClick,
	useDismiss,
	useFloating,
	useHover,
	useInteractions,
} from "@floating-ui/react";
import { cva, type VariantProps } from "class-variance-authority";
import clsx from "clsx";
import { type JSX, useState } from "react";
import { CloseButton } from "../button";
import { Portal } from "../portal";
import type { ChildrenProps, ClassNameProps } from "../types";
import { cn } from "../utils";
import { PopoverContext } from "./context";

/** Props Interface */
interface PopoverProps extends ChildrenProps, ClassNameProps, VariantProps<typeof popover> {
	render: (onClose: () => void) => JSX.Element;
	trigger: "hover" | "click";
	placement?: Placement;
	openDelay?: number;
	isDisabled?: boolean;
	withCloseButton?: boolean;
}

export function Popover({
	children,
	render,
	trigger,
	placement = "bottom-start",
	className,
	variant = "base",
	openDelay = 500,
	isDisabled = false,
	withCloseButton,
}: PopoverProps) {
	/** State */
	const [isOpen, setIsOpen] = useState<boolean>(false);

	/** Computed */
	const popoverClasses = popover({ variant });

	/** Render */
	const { refs, floatingStyles, context } = useFloating({
		placement,
		strategy: "fixed",
		open: isOpen,
		onOpenChange: setIsOpen,
		middleware: [
			offset(5),
			shift({
				padding: 15,
			}),
			flip({ mainAxis: true, crossAxis: true }),
		],
		whileElementsMounted: autoUpdate,
	});
	const click = useClick(context, {
		enabled: trigger === "click",
	});

	const hover = useHover(context, {
		enabled: trigger === "hover",
		handleClose: safePolygon({ buffer: 5 }),
		delay: {
			open: openDelay,
		},
	});
	const dismiss = useDismiss(context);
	const { getReferenceProps, getFloatingProps } = useInteractions([click, hover, dismiss]);

	/** Functions */
	function handleOnClose() {
		setIsOpen(false);
	}

	if (isDisabled) return children;

	/** Render */
	return (
		<PopoverContext.Provider value={{ isOpen, setIsOpen, handleOnClose }}>
			<div
				ref={refs.setReference}
				{...getReferenceProps()}
				className={clsx("cursor-pointer", "inline w-max h-max", className)}
			>
				{children}
			</div>
			{isOpen && (
				<Portal>
					<div
						ref={refs.setFloating}
						{...getFloatingProps()}
						style={floatingStyles}
						className={cn("z-502", popoverClasses)}
					>
						{withCloseButton && (
							<CloseButton
								onClose={handleOnClose}
								className="z-10"
							/>
						)}
						{render(() => {
							setIsOpen(false);
						})}
					</div>
				</Portal>
			)}
		</PopoverContext.Provider>
	);
}

const popover = cva([], {
	variants: {
		variant: {
			base: ["relative w-max min-w-[200px] card shadow-md"],
			plain: ["w-max"],
		},
	},
});
